import { z } from "zod";
import { accountUserRoleSchema, type Account } from "./account/index.js";
import { supervisorOrgUserRoleSchema, type SupervisorOrgUser } from "./supervisorOrg/index.js";

export const SESSION_TIMEOUT = 12 * 60 * 60;

const authKindSchema = z.enum(["admin", "supervisor", "operator", "user"]);
export type AuthKind = z.infer<typeof authKindSchema>;
export const allKinds: readonly AuthKind[] = authKindSchema.options;

const authRoleSchema = z.enum([
  "admin",
  ...supervisorOrgUserRoleSchema.options.map((role) => `supervisor.${role}` as const),
  "operator",
  ...accountUserRoleSchema.options.map((role) => `user.${role}` as const),
]);
export type AuthRole = z.infer<typeof authRoleSchema>;

export const accountManagerRoles: readonly AuthRole[] = [
  "supervisor.supervisor",
  "supervisor.nonApprovingSupervisor",
  "user.accountHolder",
];

export const supervisorRoles: readonly AuthRole[] = [
  "supervisor.supervisor",
  "supervisor.nonApprovingSupervisor",
];

export const transferRequestApproverRoles: readonly AuthRole[] = [
  "supervisor.supervisor",
  "supervisor.externalApprover",
];

export const apiKeyManagerRoles: readonly AuthRole[] = ["supervisor.supervisor"];

export const accountViewerRoles: readonly AuthRole[] = [
  ...supervisorRoles,
  "supervisor.externalApprover",
  "supervisor.reader",
  "user.accountHolder",
];

export const transferRequestCreatorRoles: readonly AuthRole[] = ["user.accountHolder"];

export const payoutCreatorRoles: readonly AuthRole[] = ["user.accountHolder"];

export const beneficiaryManagerRoles: readonly AuthRole[] = ["user.accountHolder"];

export const accountMemberManagerRoles: readonly AuthRole[] = ["user.accountHolder"];

export const kycFillerRoles: readonly AuthRole[] = ["user.accountHolder"];

export const accountCommenterRoles: readonly AuthRole[] = [
  ...accountManagerRoles,
  "supervisor.externalApprover",
];

export const allRoles: readonly AuthRole[] = authRoleSchema.options;

const sessionClaimsSchema = z.object({
  sessionExpiration: z.number().default(0),
  isMfaEnrolled: z.boolean().default(false),
  loggedInWithMfa: z.boolean().default(false),
});
const adminClaimsSchema = sessionClaimsSchema.extend({ kind: z.literal("admin") });
const supervisorClaimsSchema = sessionClaimsSchema.extend({
  kind: z.literal("supervisor"),
  supervisorOrgId: z.string(),
});
// Operators have no MFA
const operatorClaimsSchema = sessionClaimsSchema.extend({
  kind: z.literal("operator"),
  accountId: z.string(),
  isMfaEnrolled: z.literal(true).default(true),
  loggedInWithMfa: z.literal(true).default(true),
});
const userClaimsSchema = sessionClaimsSchema.extend({ kind: z.literal("user") });
const firebaseClaimsSchema = z.discriminatedUnion("kind", [
  adminClaimsSchema,
  supervisorClaimsSchema,
  operatorClaimsSchema,
  userClaimsSchema,
]);

export type AuthClaims = z.input<typeof firebaseClaimsSchema>;
export type DecodedAuthClaims = z.output<typeof firebaseClaimsSchema>;

export function decodeAuthClaims(rawClaims: Record<string, unknown>): DecodedAuthClaims {
  return firebaseClaimsSchema.parse(rawClaims);
}

export function resolveAuthRole({
  claims,
  uid,
  supervisorOrgUser,
  account,
}: {
  claims: DecodedAuthClaims;
  uid: string;
  supervisorOrgUser: SupervisorOrgUser | undefined;
  account?: { id: string; data: Account };
}): AuthRole | null {
  switch (claims.kind) {
    case "admin":
      return "admin";
    case "operator":
      return account?.id === claims.accountId ? "operator" : null;
    case "supervisor":
      if (!supervisorOrgUser) return null;
      if (account && account.data.supervisorOrgId !== claims.supervisorOrgId) return null;
      if (
        account &&
        supervisorOrgUser.role === "externalApprover" &&
        !account.data.externalApprovers?.includes(uid)
      )
        return null;
      return `supervisor.${supervisorOrgUser.role}`;
    case "user": {
      const accountUser = account?.data.users?.[uid];
      return accountUser ? `user.${accountUser.role}` : null;
    }
  }
}

/** `admin` has every role, even one not listed in `roles`. */
export const hasRole = (role: AuthRole | null, roles: readonly AuthRole[]) =>
  role === "admin" || (role !== null && roles.includes(role));
